import {
  CheckmarkSquare04Icon,
  RedoIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { useAuthContext } from "providers/Auth";
import {
  Show,
  Button,
  ButtonVariant,
  ButtonSize,
  Hide
} from "shared/components";
import { IncomeType, ApplicationStatus } from "shared/types";
import { BankStatements } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { DateTime } from "luxon";
import { EditIncomeDropdownMenu } from "./EditIncomeDropdownMenu";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { ReverifyAttempt } from "../../../ReverifySlice/types";
import { isReverifyTabABankStatement } from "../../../ReverifySlice/utils";
import { getActionsDisabledReason } from "../../utils";
import { useCombinedIncomeContext } from "views/applications-id-report/slices/CombinedIncomeReportSlice/providers/useCombinedIncome";

interface Props {
  income: CombinedIncome;
  selectedReverifyAttempt?: ReverifyAttempt;
  allBankStatements?: BankStatements;
  isIncomeIncomplete: boolean;
  hasInProgressReverify: boolean;
  hasFailedReverify: boolean;
}

export const IncomeHeader = ({
  income,
  selectedReverifyAttempt,
  allBankStatements,
  isIncomeIncomplete,
  hasInProgressReverify,
  hasFailedReverify
}: Props) => {
  const { permissions } = useAuthContext();
  const canCompleteVerification = permissions.canEditIncome;
  const {
    applicant,
    applicationId,
    applicationStatus,
    reportModals: {
      setShowEditIncomeModal,
      setShowEditTransactionsModal,
      setShowReverifyIncomeModal,
      setShowOverrideVerificationModal
    },
    isReadOnlyVerification
  } = useCombinedIncomeContext();
  const isManualApplicant = applicant.is_manual;
  const { transactions_edited_at, transactions_edited_by_name } = income;
  const isBankStatement = isReverifyTabABankStatement(selectedReverifyAttempt);
  const shouldShowTransactionsEditedWarning =
    isBankStatement && (transactions_edited_at || transactions_edited_by_name);

  const { areActionsDisabled, editDisableReason, reverifyDisableReason } =
    getActionsDisabledReason({
      hasInProgressReverify,
      hasFailedReverify,
      isIncomeIncomplete,
      applicationStatus
    });

  return (
    <div className="pb-5 mb-5 space-y-4 border-b border-alpha-40">
      <div className="flex justify-between gap-1 w-full">
        <div className="space-y-1">
          <p className="text-content-1000 font-bold text-xl">
            Income source summary
          </p>
          <p className="text-content-300 font-medium text-sm">
            You can edit or re-verify the income source details
          </p>
        </div>
        <Hide if={isReadOnlyVerification}>
          <div className="flex items-center gap-2">
            <EditIncomeDropdownMenu
              setShowEditIncomeAmountModal={setShowEditIncomeModal}
              setShowEditIncomeTransactionsDrawer={setShowEditTransactionsModal}
              selectedReverifyAttempt={selectedReverifyAttempt}
              income={income}
              allBankStatements={allBankStatements}
              disabled={areActionsDisabled}
              disabledReason={editDisableReason}
            />
            <Hide
              if={income.type === IncomeType.OfferLetter || isManualApplicant}
            >
              <Button
                variant={
                  areActionsDisabled
                    ? ButtonVariant.BASE
                    : ButtonVariant.PRIMARY
                }
                size={ButtonSize.XS}
                HugeIcon={RedoIcon}
                disabled={
                  areActionsDisabled ||
                  applicationStatus === ApplicationStatus.OnHold
                }
                tooltip={
                  applicationStatus === ApplicationStatus.OnHold ? (
                    <ResumeTooltip applicationId={Number(applicationId || 0)} />
                  ) : (
                    reverifyDisableReason || undefined
                  )
                }
                className="shadow-xs !w-[111px] !gap-2"
                tooltipDelay={250}
                onClick={() => setShowReverifyIncomeModal(income.id)}
              >
                Re-verify
              </Button>
            </Hide>

            <Show if={isIncomeIncomplete}>
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.XS}
                HugeIcon={CheckmarkSquare04Icon}
                disabled={
                  !canCompleteVerification ||
                  applicationStatus === ApplicationStatus.OnHold
                }
                tooltip={
                  canCompleteVerification ? (
                    <>
                      Verification of this income source will be marked
                      <br /> as complete, and the applicant will be able to
                      <br /> proceed to the next step
                    </>
                  ) : (
                    "You don't have the permission to complete an income verification."
                  )
                }
                tooltipDelay={250}
                onClick={() => setShowOverrideVerificationModal(income)}
              >
                Complete verification
              </Button>
            </Show>
          </div>
        </Hide>
      </div>
      <Show if={shouldShowTransactionsEditedWarning}>
        <Banner
          variant="warning"
          textTheme="base"
          body={
            <>
              Income transactions were edited by{" "}
              <b>
                {transactions_edited_by_name}, on{" "}
                {DateTime.fromISO(transactions_edited_at).toFormat(
                  "MMMM d, yyyy 'at' h:mma"
                )}
              </b>
            </>
          }
          HugeIcon={
            <HugeiconsIcon
              icon={InformationCircleIcon}
              className="shrink-0 text-warning-500"
              size="20"
            />
          }
        />
      </Show>
    </div>
  );
};
